import {
  getCoreRowModel,
  getPaginationRowModel,
  PaginationState,
  useReactTable
} from '@tanstack/react-table';
import type { TFunction } from 'i18next';
import { FC, useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type {
  WalletCsvScope,
  WalletTransaction,
  WalletTransactions
} from '../../../../queries/pxtraCard';
import {
  ArrowDownIcon,
  ChevronGrabberVerticalIcon,
  ClockIcon,
  DownloadBoxIcon
} from '../../../icons';
import { cn } from '../../../util';
import { Accordion } from '../../atomic/dataDisplay/accordion';
import Pagination from '../../tables/shared/pagination';
import { csvScopeKey } from './csv';
import {
  berlinIsoDate,
  formatCents,
  formatCoverageCents,
  formatDate,
  formatSignedCents,
  monthName
} from './format';
import { clampPageIndex, expandMonth, groupByMonth, MonthGroup } from './transactionsGrouping';

const DEFAULT_PAGE_SIZE = 25;
const NO_VALUE = '–';

type Props = {
  year: number;
  transactions: WalletTransactions;
  /** Its month group starts expanded. */
  currentMonth: string;
  /** Whether the year's table starts open. */
  defaultExpanded: boolean;
  /** The download buttons stay busy until the returned promise settles. */
  onDownload: (scope: WalletCsvScope) => Promise<void> | void;
};

type Item = { type: 'month'; group: MonthGroup } | { type: 'row'; row: WalletTransaction };

const monthScope = (year: number, month: string): WalletCsvScope => ({
  year,
  month: Number(month.slice(5, 7))
});

const receiptScope = (row: WalletTransaction): WalletCsvScope => ({
  transactionId: row.id,
  date: berlinIsoDate(row.booked_at)
});

const transactionLabel = (t: TFunction, row: WalletTransaction) => {
  if (row.kind === 'top_up') return t('pxtraCard:transactionTopUp');
  if (row.kind === 'funding' && row.source === 'monthly_benefit' && row.accounting_period) {
    return t('pxtraCard:positionMonthly', { month: monthName(row.accounting_period) });
  }
  return row.label;
};

const cell = 'px-4 py-4 text-left';
const numberCell = 'px-4 py-4 text-right whitespace-nowrap';
const iconCell = 'w-[70px] px-4 py-2 text-center';
const toggleCell = 'w-14 px-2 py-2';
const rowBorder = 'border-0 border-b border-solid border-grey-100';

const DownloadButton: FC<{ label: string; busy: boolean; onClick: () => void }> = ({
  label,
  busy,
  onClick
}) => (
  <button
    type="button"
    aria-label={label}
    aria-busy={busy}
    disabled={busy}
    onClick={onClick}
    className="mx-auto flex h-8 w-8 cursor-pointer items-center justify-center rounded-lg border-0 bg-transparent p-1 text-primary-main hover:bg-grey-50 disabled:cursor-default disabled:opacity-40 disabled:hover:bg-transparent"
  >
    <DownloadBoxIcon />
  </button>
);

const ClockBadge = () => (
  <span className="flex h-8 w-8 items-center justify-center rounded-full bg-grey-50 text-primary-main">
    <ClockIcon />
  </span>
);

export const TransactionsCard: FC<Props> = ({
  year,
  transactions,
  currentMonth,
  defaultExpanded,
  onDownload
}) => {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState<Set<string>>(() => expandMonth(new Set(), currentMonth));
  const [seenMonth, setSeenMonth] = useState(currentMonth);
  if (seenMonth !== currentMonth) {
    setSeenMonth(currentMonth);
    setExpanded(expandMonth(expanded, currentMonth));
  }
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: DEFAULT_PAGE_SIZE
  });
  const [downloading, setDownloading] = useState<ReadonlySet<string>>(() => new Set());

  const groups = useMemo(() => groupByMonth(transactions.results), [transactions.results]);
  const items = useMemo<Item[]>(
    () =>
      groups.flatMap((group) => [
        { type: 'month' as const, group },
        ...(expanded.has(group.month)
          ? group.rows.map((row) => ({ type: 'row' as const, row }))
          : [])
      ]),
    [groups, expanded]
  );

  // Collapsing a month or a refetch can shorten the list under the pager.
  const table = useReactTable<Item>({
    data: items,
    columns: [],
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    state: {
      pagination: {
        pageSize: pagination.pageSize,
        pageIndex: clampPageIndex(pagination.pageIndex, items.length, pagination.pageSize)
      }
    },
    onPaginationChange: setPagination,
    autoResetPageIndex: false
  });

  const toggle = (month: string) => {
    const next = new Set(expanded);
    if (next.has(month)) next.delete(month);
    else next.add(month);
    setExpanded(next);
  };

  const download = useCallback(
    async (scope: WalletCsvScope) => {
      const key = csvScopeKey(scope);
      setDownloading((current) => new Set(current).add(key));
      try {
        await onDownload(scope);
      } finally {
        setDownloading((current) => {
          const next = new Set(current);
          next.delete(key);
          return next;
        });
      }
    },
    [onDownload]
  );

  const isBusy = (scope: WalletCsvScope) => downloading.has(csvScopeKey(scope));

  const netCents = transactions.results.reduce((sum, row) => sum + row.amount_cents, 0);
  const personsTotal = groups.reduce((sum, group) => sum + group.personsTotal, 0);
  const receipt = t('pxtraCard:colReceipt');

  const renderItem = (item: Item) => {
    if (item.type === 'month') {
      const { group } = item;
      const isOpen = expanded.has(group.month);
      const name = monthName(group.month);
      return (
        <tr key={group.month} className={cn(rowBorder, 'font-medium')}>
          <td className={toggleCell}>
            <button
              type="button"
              aria-expanded={isOpen}
              aria-label={t('pxtraCard:toggleMonth', { month: name })}
              onClick={() => toggle(group.month)}
              className="flex h-10 w-10 cursor-pointer items-center justify-center rounded-lg border-0 bg-transparent p-2 text-primary-main hover:bg-grey-50"
            >
              <ArrowDownIcon className={cn('transition-transform', isOpen && 'rotate-180')} />
            </button>
          </td>
          <td className={cell}>{name}</td>
          <td className={cell} />
          <td className={numberCell}>{formatSignedCents(group.netCents)}</td>
          <td className={numberCell}>{formatCents(group.balanceAfterCents)}</td>
          <td className={numberCell} />
          <td className={iconCell}>
            <DownloadButton
              label={`${receipt} ${name}`}
              busy={isBusy(monthScope(year, group.month))}
              onClick={() => download(monthScope(year, group.month))}
            />
          </td>
        </tr>
      );
    }

    const { row } = item;
    return (
      <tr key={row.id} className={rowBorder}>
        <td className={toggleCell} />
        <td className={cell}>{formatDate(row.booked_at)}</td>
        <td className={cell}>{transactionLabel(t, row)}</td>
        <td className={cn(numberCell, row.amount_cents < 0 && 'text-error-main')}>
          {formatSignedCents(row.amount_cents)}
        </td>
        <td className={numberCell}>{formatCents(row.balance_after_cents)}</td>
        <td className={numberCell}>{row.recipient_count ?? NO_VALUE}</td>
        <td className={iconCell}>
          <DownloadButton
            label={`${receipt} ${transactionLabel(t, row)} ${formatDate(row.booked_at)}, ${formatCoverageCents(row.amount_cents)}`}
            busy={isBusy(receiptScope(row))}
            onClick={() => download(receiptScope(row))}
          />
        </td>
      </tr>
    );
  };

  const header = 'px-4 py-5 text-left text-xs font-medium text-grey-600';
  const numberHeader = cn(header, 'text-right');

  const body = (
    <div className="text-left">
      <div className="w-0 min-w-full overflow-x-auto">
        <table className="w-full border-collapse text-base text-primary-main">
          <thead className="bg-grey-50">
            <tr className={rowBorder}>
              <th className={toggleCell}>
                <span className="flex h-10 w-10 items-center justify-center text-primary-main">
                  <span aria-hidden="true" className="flex">
                    <ChevronGrabberVerticalIcon />
                  </span>
                  <span className="sr-only">{t('pxtraCard:colToggle')}</span>
                </span>
              </th>
              <th className={header}>{t('pxtraCard:colPeriod')}</th>
              <th className={header}>{t('pxtraCard:colKind')}</th>
              <th className={numberHeader}>{t('pxtraCard:colSignedAmount')}</th>
              <th className={numberHeader}>{t('pxtraCard:colWalletBalance')}</th>
              <th className={numberHeader}>{t('pxtraCard:colPersons')}</th>
              <th className={cn(header, 'w-[70px] text-center')}>{receipt}</th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 ? (
              <tr className={rowBorder}>
                <td colSpan={7} className="px-4 py-12 text-center">
                  <p className="m-0 font-medium">{t('pxtraCard:transactionsEmptyTitle')}</p>
                  <p className="m-0 mt-1 text-grey-500">{t('pxtraCard:transactionsEmptyText')}</p>
                </td>
              </tr>
            ) : (
              table.getRowModel().rows.map((tableRow) => renderItem(tableRow.original))
            )}
          </tbody>
          <tfoot className="font-medium">
            <tr className={rowBorder}>
              <td className={toggleCell} />
              <td className={cell}>{t('pxtraCard:total')}</td>
              <td className={cell} />
              <td className={numberCell}>
                {items.length === 0 ? formatCents(0) : formatSignedCents(netCents)}
              </td>
              <td className={numberCell}>{formatCents(transactions.closing_balance_cents)}</td>
              <td className={numberCell}>{items.length === 0 ? '' : personsTotal}</td>
              <td className={iconCell}>
                <DownloadButton
                  label={`${receipt} ${year}`}
                  busy={isBusy({ year })}
                  onClick={() => download({ year })}
                />
              </td>
            </tr>
          </tfoot>
        </table>
      </div>
      <div className="flex justify-end px-2">
        <Pagination table={table} />
      </div>
    </div>
  );

  return (
    <section className="min-w-0 overflow-hidden rounded-2xl border border-solid border-grey-100 bg-white [&_.MuiAccordionSummary-content_p]:text-lg [&_.MuiAccordionSummary-content_p]:font-medium [&_.MuiAccordionSummary-content_p]:text-primary-main">
      <Accordion
        defaultExpanded={defaultExpanded}
        withHeadlinePadding
        startIcon={<ClockBadge />}
        headlineData={{ mainText: t('pxtraCard:transactionsYear', { year }) }}
      >
        {body}
      </Accordion>
    </section>
  );
};
